/**
 * @brisa/ui · facelift/avatar-clube/AvatarClube
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #15 (avatar-clube)
 * Figma origem: node 163:920
 *
 * Wrapper composicional sobre Avatar shadcn — sizes sm(32) / md(40) / lg(56)
 * + alt text canônico "Logo do <club_name>". Drop-shadow stack canônico do
 * Figma (4 camadas) aplicado por default — consumidor pode anular passando
 * `withShadow={false}`. Sem 'use client' (apresentacional).
 */
import * as React from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { cn } from '@brisa/ui/lib/utils';

export type AvatarClubeSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<AvatarClubeSize, string> = {
  sm: 'h-8 w-8', // 32px (mini-card mapa)
  md: 'h-10 w-10', // 40px — default + ClubCardFacelift
  lg: 'h-14 w-14', // 56px — EventCardHorizontal (Bruno 2026-05-14)
};

// Stack canônico do Figma 163:920 — 4 camadas drop-shadow simulando elevação
// suave de logo recortado. Mantém a forma circular do avatar (drop-shadow,
// não box-shadow, pra acompanhar o alfa do PNG).
const SHADOW_FILTER =
  '[filter:drop-shadow(0_18px_7px_rgba(0,0,0,0.01))_drop-shadow(0_10px_6px_rgba(0,0,0,0.05))_drop-shadow(0_4px_4px_rgba(0,0,0,0.09))_drop-shadow(0_1px_2px_rgba(0,0,0,0.1))]';

export interface AvatarClubeProps {
  club: {
    name: string;
    avatar_url?: string | null;
  };
  size?: AvatarClubeSize;
  withShadow?: boolean;
  className?: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function AvatarClube({
  club,
  size = 'md',
  withShadow = true,
  className,
}: AvatarClubeProps) {
  return (
    <Avatar
      data-slot="avatar"
      className={cn(
        'shrink-0 rounded-full',
        SIZE_CLASSES[size],
        withShadow ? SHADOW_FILTER : '',
        className,
      )}
    >
      {club.avatar_url ? (
        <AvatarImage src={club.avatar_url} alt={`Logo do ${club.name}`} />
      ) : null}
      <AvatarFallback>{initials(club.name)}</AvatarFallback>
    </Avatar>
  );
}
